import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Alert,
  Link as MuiLink,
  CircularProgress,
  InputAdornment,
  IconButton,
} from '@mui/material';
import { Lock, Visibility, VisibilityOff, CheckCircle } from '@mui/icons-material';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import api from '../services/api';

const ResetPasswordPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [tokenEmail, setTokenEmail] = useState('');

  useEffect(() => {
    verifyToken();
  }, [token]);

  const verifyToken = async () => {
    if (!token) {
      setError('Invalid password reset link. Please request a new one.');
      setVerifying(false);
      return;
    }

    try {
      const response = await api.post(`/password/verify-reset-token?token=${token}`);

      if (response.data.valid) {
        setTokenValid(true);
        setTokenEmail(response.data.email);
      } else {
        setError(response.data.message || 'Invalid or expired reset token');
      }
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to verify reset token');
    } finally {
      setVerifying(false);
    }
  };

  const validatePassword = () => {
    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters long');
      return false;
    }

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!validatePassword()) {
      return;
    }

    setLoading(true);

    try {
      const response = await api.post('/password/reset-password', {
        token,
        new_password: newPassword,
      });

      if (response.data.success) {
        setSuccess(true);
        setTimeout(() => {
          navigate('/login');
        }, 3000);
      }
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to reset password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (verifying) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        }}
      >
        <Paper elevation={10} sx={{ p: 6, textAlign: 'center', borderRadius: 2 }}>
          <CircularProgress size={60} sx={{ mb: 2 }} />
          <Typography variant="h6">Verifying reset link...</Typography>
        </Paper>
      </Box>
    );
  }

  if (!tokenValid && !verifying) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        }}
      >
        <Container maxWidth="sm">
          <Paper elevation={10} sx={{ p: 4, textAlign: 'center', borderRadius: 2 }}>
            <Lock sx={{ fontSize: 80, color: '#f44336', mb: 2 }} />
            <Typography variant="h4" fontWeight="bold" gutterBottom color="error">
              Invalid Reset Link
            </Typography>
            <Typography variant="body1" color="text.secondary" paragraph>
              {error || 'This password reset link is invalid or has expired.'}
            </Typography>

            <Alert severity="info" sx={{ mb: 3, textAlign: 'left' }}>
              <Typography variant="body2">
                <strong>Reset links expire after 1 hour.</strong><br />
                Please request a new password reset link.
              </Typography>
            </Alert>

            <Button
              variant="contained"
              component={Link}
              to="/forgot-password"
              size="large"
              sx={{
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              }}
            >
              Request New Reset Link
            </Button>
          </Paper>
        </Container>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        py: 4,
      }}
    >
      <Container maxWidth="sm">
        <Paper elevation={10} sx={{ p: 4, borderRadius: 2 }}>
          {!success ? (
            <>
              {/* Header */}
              <Box sx={{ textAlign: 'center', mb: 4 }}>
                <Lock sx={{ fontSize: 60, color: '#667eea', mb: 2 }} />
                <Typography variant="h4" fontWeight="bold" gutterBottom>
                  Reset Your Password
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  Create a new password for <strong>{tokenEmail}</strong>
                </Typography>
              </Box>

              {/* Error Alert */}
              {error && (
                <Alert severity="error" sx={{ mb: 3 }}>
                  {error}
                </Alert>
              )}

              {/* Form */}
              <form onSubmit={handleSubmit}>
                <TextField
                  fullWidth
                  label="New Password"
                  type={showPassword ? 'text' : 'password'}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                  disabled={loading}
                  sx={{ mb: 2 }}
                  autoFocus
                  placeholder="Enter new password"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPassword(!showPassword)}
                          edge="end"
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  helperText="Minimum 8 characters"
                />

                <TextField
                  fullWidth
                  label="Confirm New Password"
                  type={showPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  disabled={loading}
                  sx={{ mb: 3 }}
                  placeholder="Confirm new password"
                />

                <Alert severity="info" sx={{ mb: 3 }}>
                  <Typography variant="body2">
                    <strong>Password Requirements:</strong><br />
                    • At least 8 characters long<br />
                    • Use a mix of letters, numbers, and symbols<br />
                    • Don't reuse old passwords
                  </Typography>
                </Alert>

                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  size="large"
                  disabled={loading}
                  sx={{
                    py: 1.5,
                    mb: 2,
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #5568d3 0%, #653b8a 100%)',
                    },
                  }}
                >
                  {loading ? (
                    <CircularProgress size={24} color="inherit" />
                  ) : (
                    'Reset Password'
                  )}
                </Button>

                <Box sx={{ textAlign: 'center' }}>
                  <MuiLink
                    component={Link}
                    to="/login"
                    sx={{
                      textDecoration: 'none',
                      color: '#667eea',
                      '&:hover': { textDecoration: 'underline' },
                    }}
                  >
                    Back to Login
                  </MuiLink>
                </Box>
              </form>
            </>
          ) : (
            <>
              {/* Success Message */}
              <Box sx={{ textAlign: 'center' }}>
                <CheckCircle sx={{ fontSize: 80, color: '#4caf50', mb: 2 }} />
                <Typography variant="h4" fontWeight="bold" gutterBottom color="success.main">
                  Password Reset Successful!
                </Typography>
                <Typography variant="body1" color="text.secondary" paragraph>
                  Your password has been reset successfully.
                </Typography>

                <Alert severity="success" sx={{ mb: 3 }}>
                  <Typography variant="body2">
                    You will be redirected to the login page in 3 seconds...<br />
                    Or click the button below to log in now.
                  </Typography>
                </Alert>

                <Button
                  variant="contained"
                  component={Link}
                  to="/login"
                  size="large"
                  sx={{
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  }}
                >
                  Go to Login
                </Button>
              </Box>
            </>
          )}
        </Paper>

        {/* Footer */}
        <Box sx={{ mt: 3, textAlign: 'center' }}>
          <Typography variant="body2" sx={{ color: 'white' }}>
            © 2025 Dure Technologies Pvt Ltd. All rights reserved.
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default ResetPasswordPage;
